import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
    Avatar,
    Box,
    FormControl,
    Grid2,
    MenuItem,
    Paper,
    Select,
    Stack,
    styled,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CommonTable } from '../../components/common-styles';
import { IUserData } from '../../service/deviceListService';
import { RootState } from '../../store';
import { CustomTabs, DashboardWrapper, NotifyContainer, UserProfilePicWrapper } from './home-styles';
import ProfileView from './profile-view';
import { ICustomPanelProps, IStaticsCardItem, IStaticsCardProps } from './type';
import { useAdminDashboard } from './useAdminDashboard';
import UserDetailListPopup from './userDetailPopUp';

interface HomeProps {}

interface TabPanelProps {
    children: React.ReactNode;
    index: number;
    value: number;
}
const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            height="calc(100dvh - 180px)"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            sx={{
                '& .cstTabHeader': {
                    position: 'absolute',
                    top: '14px',
                    left: 0,
                    width: '50%',
                },
            }}
        >
            {value === index && <Box>{children}</Box>}
        </Box>
    );
};

const OverlappingAvatar = styled(Avatar)(() => ({
    position: 'relative',
    width: 30,
    height: 30,
    zIndex: 1,
    '&:not(:first-of-type)': {
        marginLeft: -12,
    },
}));

const CustomNoGpuStateRowsOverlay = (): React.ReactElement => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'gray',
            }}
        >
            <Typography>{activeHTTPRequests > 0 ? t('data_fetching') : t('gpu_stat_data_unavailable')}</Typography>
        </Box>
    );
};

const CustomNoConnectivityRowsOverlay = (): React.ReactElement => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 200,
                color: 'gray',
            }}
        >
            <Typography>{activeHTTPRequests > 0 ? t('data_fetching') : t('connectivity_data_unavailable')}</Typography>
        </Box>
    );
};
export const Home: React.FC<HomeProps> = () => {
    const { t } = useTranslation();
    const {
        deviceList,
        selectedDevice,
        handleDeviceChange,
        dashboardData,
        value,
        setValue,
        onUserPopUpCloseHandler,
        userList,
        gpuStateData,
        connectivityStatusData,
        handleAvatarClick,
        anchorEl,
    } = useAdminDashboard();

    const gpuStateColumn: GridColDef[] = [
        { field: 'month', headerName: t('month'), flex: 1 },
        { field: 'gpu_utilization', headerName: t('gpu_utilization'), flex: 1 },
        { field: 'earnings', headerName: t('earning'), flex: 1 },
    ];
    const connectivityColumns: GridColDef[] = [
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: params => {
                return (
                    <Stack flexDirection="row" alignItems="center" display="flex" height="100%">
                        <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
                            {t('status')}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            pl={1}
                            sx={{
                                fontWeight: 'bold',
                                color: params?.value === 0 ? '#EF4444' : '#22C55E',
                                fontSize: '14px',
                            }}
                        >
                            {params?.value === 0 ? t('disconnected') : t('connected')}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            field: 'date',
            headerName: 'Date & Time',
            flex: 1,
            headerAlign: 'right',
            align: 'right',
            renderCell: params => {
                return (
                    <Stack
                        flexDirection="row"
                        alignItems="center"
                        display="flex"
                        height="100%"
                        justifyContent="flex-end"
                        width="100%"
                    >
                        <Stack flexDirection="row" alignItems="center">
                            <span style={{ marginTop: '5px' }}>
                                <CalendarMonthIcon fontSize="small" />
                            </span>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontSize: '14px', textAlign: 'right', minWidth: 180 }}
                            >
                                {params?.value}
                            </Typography>
                        </Stack>
                    </Stack>
                );
            },
        },
    ];

    const renderStatusCard = ({ heading, values, isCenter = true }: IStaticsCardProps) => {
        const renderValue = ({ value, text, color, subtitle }: IStaticsCardItem) => (
            <Box key={text} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: color || '#2FABE1' }}>
                    {value}
                </Typography>
                <Typography sx={{ fontSize: 12, color: color || '#2FABE1' }}>{text}</Typography>
                {subtitle && (
                    <Typography sx={{ fontSize: 10, color: color || '#2FABE1', mt: 0.5 }}>{subtitle}</Typography>
                )}
            </Box>
        );

        return (
            <Box className="listBox">
                <Typography variant="subtitle2">{heading}</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        justifyContent: isCenter ? 'center' : 'space-between',
                        paddingTop: 1,
                    }}
                >
                    {values.map(renderValue)}
                </Box>
            </Box>
        );
    };

    const isHaveValue = (value: string | number | undefined) => {
        return value !== null && value !== undefined;
    };

    // Helper to format card values
    const formatCardValue = (value: string | number | undefined, suffix = '', fallback = '--') => {
        if (!isHaveValue(value)) return fallback;
        return suffix ? `${value}${suffix}` : `${value}`;
    };

    // Card value generators
    const getDeviceStatusValues = React.useCallback(
        (device_status: any) => [
            {
                value: formatCardValue(device_status?.active),
                text: t('active'),
                color: '#4DA6FF',
            },
            {
                value: formatCardValue(device_status?.poweroff),
                text: t('powered_off'),
                color: '#F44336',
            },
            {
                value: formatCardValue(device_status?.inactive),
                text: t('no_network'),
                color: '#F44336',
            },
        ],
        [t],
    );

    const getGpuUsageValues = React.useCallback(
        (gpu: any) => [
            {
                value: formatCardValue(gpu?.current_month, '%'),
                text: isHaveValue(gpu?.devices_count) ? `${gpu?.devices_count} ${t('devices')}` : '--',
                color: '#4DA6FF',
            },
        ],
        [t],
    );

    const getTempValues = React.useCallback(
        (temperature: any) => [
            {
                value: formatCardValue(temperature?.current_month, '°F'),
                text: isHaveValue(temperature?.devices_count) ? `${temperature?.devices_count} ${t('devices')}` : '--',
                color: '#4DA6FF',
            },
        ],
        [t],
    );

    const getEarningsValues = React.useCallback(
        (earnings: any) => [
            {
                value: isHaveValue(earnings?.current_month) ? `$${earnings?.current_month?.toFixed(2)}` : '--',
                text: t('this_month'),
                subtitle: '',
                color: '#4DA6FF',
            },
            {
                value: isHaveValue(earnings?.lifetime) ? `$${earnings?.lifetime?.toFixed(2)}` : '--',
                text: t('life_time'),
                subtitle: '',
                color: '#4DA6FF',
            },
        ],
        [t],
    );

    const renderCardView = React.useCallback((): React.ReactElement => {
        const { device_status, earnings, gpu, temperature } = dashboardData ?? {};

        const cardDataSet: IStaticsCardProps[] = [
            {
                heading: t('device_fleet_status'),
                values: getDeviceStatusValues(device_status),
            },
            {
                heading: t('overall_gpu_usage'),
                values: getGpuUsageValues(gpu),
            },
            {
                heading: t('avg_temp'),
                values: getTempValues(temperature),
            },
            {
                heading: t('total_fleet_earnings'),
                values: getEarningsValues(earnings),
                isCenter: false,
            },
        ];

        return (
            <Box textAlign="center" className="indexBody indexBodyLoader" pb={2}>
                <Grid2
                    container
                    spacing={3}
                    pt={3}
                    sx={{
                        flexWrap: {
                            xs: 'warp',
                            sm: 'warp',
                            md: 'nowrap',
                            lg: 'nowrap',
                        },
                    }}
                >
                    {cardDataSet.map(({ heading, values, isCenter }, index) => (
                        <Grid2
                            size={{ xs: 12, sm: 12, md: 3 }}
                            key={heading}
                            sx={index === 0 || index === cardDataSet.length - 1 ? { minWidth: 300 } : { minWidth: 180 }}
                        >
                            {renderStatusCard({ heading, values, isCenter })}
                        </Grid2>
                    ))}
                </Grid2>
            </Box>
        );
    }, [dashboardData, t, getDeviceStatusValues, getGpuUsageValues, getTempValues, getEarningsValues]);

    const renderUserPicList = (): React.ReactElement => {
        return (
            <UserProfilePicWrapper>
                <Box className="userProfilePicContainer">
                    {userList.map((item: IUserData) => {
                        if (!item || item === null) return null;
                        const { id, profile_image } = item ?? {};
                        return <ProfileView key={id} profile_image={profile_image} className="userProfilePic" />;
                    })}
                    <Stack className="userProfilePicBackgroundHover">
                        <OverlappingAvatar
                            className="userProfilePic"
                            onClick={handleAvatarClick}
                            data-testid="view-more"
                        >
                            <Typography variant="subtitle1">...</Typography>
                        </OverlappingAvatar>
                    </Stack>
                </Box>
            </UserProfilePicWrapper>
        );
    };

    const renderDeviceDropDown = () => {
        return (
            <FormControl className="cstSelectProfile ">
                <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    placeholder={`${t('country_code')}`}
                    className="cstEditProfile"
                    value={selectedDevice}
                    onChange={handleDeviceChange}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 200,
                            },
                        },
                    }}
                >
                    {deviceList?.map((item: any) => (
                        <MenuItem key={item.id} value={item.device_id}>
                            {item?.device_id}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const renderConnectivityDataTable = (): React.ReactElement => {
        if (!connectivityStatusData || connectivityStatusData?.length === 0) return <CustomNoConnectivityRowsOverlay />;
        return (
            <NotifyContainer>
                <Stack rowGap={1.5}>
                    {connectivityStatusData.map(({ date, status, id }: any) => (
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            className="cards"
                            key={id}
                        >
                            <Stack flexDirection="row" alignItems="center" display="flex" height="100%">
                                <Typography variant="subtitle2" sx={{ fontSize: '10px' }}>
                                    {t('status')}
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    pl={1}
                                    sx={{
                                        fontWeight: 'bold',
                                        color: status === 0 ? '#EF4444' : '#22C55E',
                                        fontSize: '14px',
                                    }}
                                >
                                    {status === 0 ? t('disconnected') : t('connected')}
                                </Typography>
                            </Stack>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                display="flex"
                                height="100%"
                                justifyContent="flex-end"
                                width="100%"
                            >
                                <Stack flexDirection="row" alignItems="center">
                                    <span style={{ marginTop: '5px' }}>
                                        <CalendarMonthIcon fontSize="small" sx={{ fontSize: '18px' }} />
                                    </span>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontSize: '12px', textAlign: 'right', minWidth: 150 }}
                                    >
                                        {date}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </NotifyContainer>
        );
    };

    const renderTabPanelView = ({
        index,
        heading,
        subheading,
        columns,
        dataRows,
    }: ICustomPanelProps): React.ReactElement => {
        return (
            <CustomTabPanel value={value} index={index}>
                <Stack direction="row" alignItems="center" gap={1} className="cstTabHeader">
                    <Typography
                        variant="h6"
                        color="info.500"
                        fontWeight={700}
                        letterSpacing={0.5}
                        fontFamily="good_sansmedium"
                    >
                        {heading}:
                    </Typography>
                    <Typography variant="h6" color="info.500" fontWeight={700} fontFamily="good_sansmedium">
                        {subheading}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                    {renderDeviceDropDown()}
                    {renderUserPicList()}
                </Stack>

                {value === 0 ? (
                    <CommonTable sx={{ backgroundColor: 'transparent' }}>
                        <Paper sx={{ width: '100%' }}>
                            <DataGrid
                                columns={columns}
                                rows={dataRows}
                                disableColumnMenu
                                disableColumnSorting
                                hideFooter
                                slots={{
                                    noRowsOverlay:
                                        value === 0 ? CustomNoGpuStateRowsOverlay : CustomNoConnectivityRowsOverlay,
                                }}
                            />
                        </Paper>
                    </CommonTable>
                ) : (
                    <CommonTable sx={{ backgroundColor: 'transparent' }}>{renderConnectivityDataTable()}</CommonTable>
                )}
            </CustomTabPanel>
        );
    };

    const renderTableHeadingView = (): React.ReactElement => {
        const a11yProps = (index: number) => {
            return {
                id: `simple-tab-${index}`,
                'aria-controls': `simple-tabpanel-${index}`,
            };
        };
        const handleChange = (event: React.SyntheticEvent, newValue: number) => {
            setValue(newValue);
        };
        return (
            <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" width="100%">
                <CustomTabs>
                    <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label={t('gpu_stats')} {...a11yProps(0)} />
                        <Tab label={t('Connectivity_Status')} {...a11yProps(1)} />
                    </Tabs>
                </CustomTabs>
            </Stack>
        );
    };

    const renderTableView = (): React.ReactElement => {
        return (
            <Box position="relative">
                {renderTableHeadingView()}
                {renderTabPanelView({
                    index: 0,
                    heading: t('gpu_utilization_stats'),
                    subheading: t('last_six_months'),
                    columns: gpuStateColumn,
                    dataRows: gpuStateData,
                })}
                {renderTabPanelView({
                    index: 1,
                    heading: t('device_connectivity'),
                    subheading: t('last_seven_days'),
                    columns: connectivityColumns,
                    dataRows: connectivityStatusData,
                })}
            </Box>
        );
    };

    return (
        <DashboardWrapper>
            <Box>
                {renderCardView()}
                {renderTableView()}
                <UserDetailListPopup anchorEl={anchorEl} closePopup={onUserPopUpCloseHandler} userList={userList} />
            </Box>
        </DashboardWrapper>
    );
};
